import { useNavigate } from 'react-router-dom'
import { Box } from '@mui/material'
import PublicIcon from '@mui/icons-material/Public'
import { usePublishedSchedules } from './Scheduler.data'
import { FONT_SANS, SchChip, SchDisplay, TOKENS } from './SchedulerDesign'
import { SchedulerPageFrame } from './SchedulerBrandBar'

const VARIANT_LABELS: Record<string, string> = {
  balanced: 'Balanced',
  fastest: 'Fastest',
  fewest_cleans: 'Fewest cleans',
  least_disruption: 'Least disruption',
}

/** `2026-09-21` -> `21.09.2026`. */
const ddmmyyyy = (iso: string): string => {
  const [y, m, d] = (iso || '').split('-')
  return y && m && d ? `${d}.${m}.${y}` : iso || '—'
}

const dateTime = (iso: string): string => {
  const t = new Date(iso)
  return Number.isNaN(t.getTime()) ? '—' : t.toLocaleString()
}

/**
 * Every published plan — client deck 2026-09-16, slide 8.
 *
 * "Most recent at the top of the list. Details: Schedule Start Date, Schedule
 * Horizon, Schedule Variant, Schedule Version, Published By, Published Date."
 * Horizon here is the week-commencing date of the published week, which is
 * what a planner actually looks for: one row per week, because a horizon is a
 * multi-select and each week is independently supersedable.
 *
 * This is also the **Viewer's home screen**. They can open a published plan
 * and read every report on it, but nothing here leads to an editor — the row
 * navigates to the output view, never to the input tables.
 */
export const PublishedSchedulesPage = () => {
  const navigate = useNavigate()
  const { data: published = [], isLoading, isError } = usePublishedSchedules()

  return (
    <SchedulerPageFrame
      crumbs={[{ label: 'Published schedules' }]}
      bodySx={{ px: 3, py: 2.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.25 }}>
        <PublicIcon sx={{ fontSize: 18, color: TOKENS.brandTeal }} />
        <SchDisplay>Published schedules</SchDisplay>
      </Box>
      <Box sx={{ fontSize: 12, color: TOKENS.ink400, mb: 2 }}>
        The plans everyone is working from
      </Box>

      <Box
        sx={{
          border: `1px solid ${TOKENS.line200}`,
          borderRadius: '2px',
          background: TOKENS.surface0,
          overflow: 'hidden',
        }}
      >
        <Row head>
          <Box>Week commencing</Box>
          <Box>Schedule</Box>
          <Box>Variant</Box>
          <Box>Version</Box>
          <Box>Published by</Box>
          <Box>Published</Box>
        </Row>

        {isLoading && <Msg>Loading…</Msg>}
        {isError && !isLoading && (
          <Msg>Could not load published schedules.</Msg>
        )}
        {!isLoading && !isError && published.length === 0 && (
          <Msg>
            Nothing has been published yet. A Super User publishes a plan from
            the schedule&apos;s own page.
          </Msg>
        )}

        {published.map((p) => (
          <Row
            key={p.id}
            onClick={() =>
              navigate(
                `/plan-scheduler/output/${p.schedulerId}/${p.iteration}?week=${p.weekNumber}&scheduleVariant=${p.variantKey}`
              )
            }
            dim={!p.isCurrent}
          >
            <Box sx={{ fontWeight: 600 }}>
              {ddmmyyyy(p.weekCommencing)}
              {!p.isCurrent && (
                // Superseded rows stay visible. A planner who published this
                // morning needs to see that a colleague replaced it, not find
                // their row silently gone.
                <SchChip tone="neutral" sx={{ ml: '8px' }}>
                  Superseded
                </SchChip>
              )}
            </Box>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {p.scheduleName || p.schedulerId}
            </Box>
            <Box>{VARIANT_LABELS[p.variantKey] ?? p.variantKey}</Box>
            <Box>{p.iteration}</Box>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {p.publishedBy || '—'}
            </Box>
            <Box>{dateTime(p.publishedAt)}</Box>
          </Row>
        ))}
      </Box>
    </SchedulerPageFrame>
  )
}

const COLS = '150px 1fr 140px 90px 1fr 170px'

const Row = ({
  head,
  dim,
  onClick,
  children,
}: {
  head?: boolean
  dim?: boolean
  onClick?: () => void
  children: React.ReactNode
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'grid',
      gridTemplateColumns: COLS,
      gap: '12px',
      alignItems: 'center',
      px: '14px',
      py: head ? '9px' : '10px',
      background: head ? TOKENS.panelHead : 'transparent',
      borderBottom: `1px solid ${head ? TOKENS.line200 : TOKENS.line100}`,
      fontFamily: FONT_SANS,
      fontSize: head ? 10.5 : 12,
      fontWeight: head ? 700 : 400,
      textTransform: head ? 'uppercase' : 'none',
      letterSpacing: head ? '0.05em' : 0,
      color: head ? TOKENS.ink400 : TOKENS.ink900,
      opacity: dim ? 0.55 : 1,
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': onClick ? { background: TOKENS.blue50 } : undefined,
    }}
  >
    {children}
  </Box>
)

const Msg = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      px: '14px',
      py: '18px',
      fontFamily: FONT_SANS,
      fontSize: 12,
      color: TOKENS.ink400,
    }}
  >
    {children}
  </Box>
)
